/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from './../../message';
import { IDescriptorBody } from '../../descriptor';
import Comment, { ICommentJSON, IComment } from '../descriptors/commentdescriptor';

export interface IGetCommentThreadResponse extends IMessage {
	commentThreadId: string;
	comments?: string[];
	commentsV2?: ICommentJSON[];
	attributes?: string | null;
	resolvedBy?: string | null;
	context?: string | null;
	createdAt?: string;
	deletedAt?: string | null;
	resolvedAt?: string | null;
	unlinkedAt?: string | null;
}

export default class GetCommentThreadResponse extends Message {
	public readonly comments: IComment[];

	public readonly	createdAt?: Date;

	public readonly	deletedAt?: Date | null;

	public readonly	resolvedAt?: Date | null;

	public readonly attributes?: Record<string, unknown> | null;

	public readonly context?: Record<string, unknown> | null;

	public readonly unlinkedAt?: Date | null;

	public static readonly DESCRIPTOR_NAME: string = 'CommentsV2GetCommentThreadResponse';

	public static readonly DESCRIPTOR: IDescriptorBody = {
		fields: {
			commentThreadId: {
				type: 'string',
				id: 1
			},
			comments: {
				type: 'string',
				rule: 'repeated',
				id: 2
			},
			commentsV2: {
				type: 'CommentDescriptor',
				rule: 'repeated',
				id: 3
			},
			resolvedAt: {
				type: 'string',
				id: 4
			},
			resolvedBy: {
				type: 'string',
				id: 5
			},
			context: {
				type: 'string',
				id: 6
			},
			createdAt: {
				type: 'string',
				id: 7
			},
			deletedAt: {
				type: 'string',
				id: 8
			},
			attributes: {
				type: 'string',
				id: 9
			},
			unlinkedAt: {
				type: 'string',
				id: 10
			}
		}
	};

	public constructor(
		public readonly commentThreadId: string,
		private readonly _comments?: IComment[],
		private readonly _commentsV2?: IComment[],
		attributes?: string | null,
		public readonly	resolvedBy?: string | null,
		context?: string | null,
		createdAt?: string,
		deletedAt?: string | null,
		resolvedAt?: string | null,
		unlinkedAt?: string | null
	) {
		super();

		this.comments = _commentsV2?.length ? _commentsV2! : _comments!;
		this.createdAt = createdAt ? new Date( createdAt ) : undefined;
		this.deletedAt = typeof deletedAt === 'string' ? new Date( deletedAt ) : deletedAt;
		this.resolvedAt = typeof resolvedAt === 'string' ? new Date( resolvedAt ) : resolvedAt;
		this.unlinkedAt = typeof unlinkedAt === 'string' ? new Date( unlinkedAt ) : unlinkedAt;
		this.attributes = typeof attributes === 'string' ? JSON.parse( attributes ) : attributes;
		this.context = typeof context === 'string' ? JSON.parse( context ) : context;
	}

	public toJSON(): IGetCommentThreadResponse {
		return {
			commentThreadId: this.commentThreadId,
			comments: this._comments?.map( comment => JSON.stringify( comment ) ),
			commentsV2: this._commentsV2?.map( Comment.toJSON ),
			attributes: this.attributes ? JSON.stringify( this.attributes ) : this.attributes,
			context: this.context ? JSON.stringify( this.context ) : this.context,
			resolvedBy: this.resolvedBy,
			createdAt: this.createdAt?.toISOString(),
			deletedAt: this.deletedAt?.toISOString(),
			resolvedAt: this.resolvedAt?.toISOString(),
			unlinkedAt: this.unlinkedAt?.toISOString()
		};
	}

	public static fromJSON( object: IGetCommentThreadResponse ): GetCommentThreadResponse {
		return new GetCommentThreadResponse(
			object.commentThreadId,
			object.comments?.map( comment => _mapOldComment( comment ) ),
			object.commentsV2?.map( Comment.fromJSON ),
			object.attributes ? JSON.parse( object.attributes ) : object.attributes,
			object.resolvedBy,
			object.context ? JSON.parse( object.context ) : object.context,
			object.createdAt,
			object.deletedAt,
			object.resolvedAt,
			object.unlinkedAt
		);
	}
}

function _mapOldComment( commentString: string ): IComment {
	const comment: IComment = JSON.parse( commentString );

	return {
		...comment,
		createdAt: new Date( comment.createdAt )
	};
}
